import React, { useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import BluetoothSearchingIcon from '@mui/icons-material/BluetoothSearching';
import { useTranslation } from 'react-i18next';

function sendAppMessage(cmd) {
  if (window?.ReactNativeWebView?.postMessage) {
    window.ReactNativeWebView.postMessage(JSON.stringify(cmd));
  } else if (window?.Android?.sendAppMessage) {
    window.Android.sendAppMessage(JSON.stringify(cmd));
  } else if (window?.webkit?.messageHandlers?.ipscAppMessageHandler) {
    window.webkit.messageHandlers.ipscAppMessageHandler.postMessage(JSON.stringify(cmd));
  } else {
    window.postMessage(JSON.stringify(cmd), '*');
  }
}

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => { ref.current = value; });
  return ref.current;
}

export default function ScoringBluetooth({ open, onUpdateScore }) {
  const { t } = useTranslation();
  const [callbackId, setCallbackId] = useState(null);
  const [selected, setSelected] = useState(() => {
    const cache = localStorage.getItem('__scoring_bluetooth_cache');
    if (cache) {
      try {
        const parsed = JSON.parse(cache);
        if (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) return parsed.device;
      } catch {}
    }
    return null;
  });
  const [devices, setDevices] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventsIndex, setEventsIndex] = useState(null);
  const [canStart, setCanStart] = useState(true);

  const prevOpen = usePrevious(open);
  const prevSelected = usePrevious(selected);

  useEffect(() => {
    const cb = Math.random().toString();
    setCallbackId(cb);
    return () => {
      sendAppMessage({ src: 'bluetooth', type: 'stop-search' });
      sendAppMessage({ src: 'bluetooth', type: 'stop-listening', params: { callback: cb } });
    };
  }, []);

  useEffect(() => {
    if (!callbackId) return;
    window[callbackId] = (response) => {
      if (response.type === 'search-result' && open && !selected) {
        setDevices(response.devices || []);
      }
      if (response.type === 'event' && selected) {
        const parsed = (response.event || '').replace(/\n/, '').split(',');
        if (parsed[0] === 'STANDBY') {
          setEvents([]);
          setEventsIndex(null);
          setCanStart(true);
        } else if (parsed[0].match(/^\d+$/)) {
          setEvents((prev) => {
            const newEvents = [...prev, { index: parseInt(parsed[0], 10), time: parseInt(parsed[2], 10) }]
              .sort((a, b) => a.index - b.index);
            setEventsIndex(newEvents.length - 1);
            return newEvents;
          });
          setCanStart(false);
        }
      }
    };
  }, [callbackId, open, selected]);

  useEffect(() => {
    if (prevOpen === undefined) return;
    if (!prevOpen && open) {
      const cache = localStorage.getItem('__scoring_bluetooth_cache');
      if (cache) {
        const parsed = JSON.parse(cache);
        if (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          setSelected(parsed.device);
          return;
        }
      }
      sendAppMessage({ src: 'bluetooth', type: 'search', params: { callback: callbackId } });
    }
    if (prevOpen && !open) {
      sendAppMessage({ src: 'bluetooth', type: 'stop-search' });
    }
  }, [open]);

  useEffect(() => {
    if (prevSelected === undefined) return;
    if (!prevSelected && selected) {
      sendAppMessage({ src: 'bluetooth', type: 'stop-search' });
      sendAppMessage({ src: 'bluetooth', type: 'listen', params: { deviceId: selected.id, callback: callbackId } });
    }
    if (prevSelected && !selected) {
      sendAppMessage({ src: 'bluetooth', type: 'stop-listening', params: { callback: callbackId } });
      sendAppMessage({ src: 'bluetooth', type: 'search', params: { callback: callbackId } });
    }
    if (prevSelected && selected && prevSelected.id !== selected.id) {
      sendAppMessage({ src: 'bluetooth', type: 'stop-listening', params: { callback: callbackId } });
      sendAppMessage({ src: 'bluetooth', type: 'listen', params: { deviceId: selected.id, callback: callbackId } });
    }
  }, [selected]);

  // Start listening immediately when restored from cache
  useEffect(() => {
    if (callbackId && selected) {
      sendAppMessage({ src: 'bluetooth', type: 'listen', params: { deviceId: selected.id, callback: callbackId } });
    }
  }, [callbackId]);
  const onTimerStart = () => {
    setEvents([]);
    setEventsIndex(null);
    sendAppMessage({ src: 'bluetooth', type: 'timer-start', params: { deviceId: selected.id } });
  };

  const onTimerReset = () => {
    setEvents([]);
    setEventsIndex(null);
    sendAppMessage({ src: 'bluetooth', type: 'timer-reset', params: { deviceId: selected.id } });
  };

  if (!open) return null;

  return (
    <Box sx={{ width: '100%', p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <BluetoothSearchingIcon fontSize="small" sx={{ mt: 0.75 }} />

      {!selected && (
        <Box>
          <Typography variant="body1">{t('scoring.haveBluetooth')}</Typography>
          {devices.length === 0 && (
            <Typography variant="caption">{t('scoring.haveBluetoothSearch')}</Typography>
          )}
          {devices.length > 0 && (
            <Typography variant="caption">{t('scoring.haveBluetoothSearchResults')}</Typography>
          )}
          {devices.map((device) => (
            <Button
              key={device.id} fullWidth color="primary" sx={{ mt: 0.5 }}
              onClick={() => {
                localStorage.setItem('__scoring_bluetooth_cache', JSON.stringify({ device, timestamp: Date.now() }));
                setDevices([]);
                setSelected(device);
              }}
            >
              {device.name}
            </Button>
          ))}
        </Box>
      )}

      {selected && (
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body1">
              {t('scoring.bluetoothConnected', { name: selected.name })}
            </Typography>
            <Button size="small" onClick={() => { localStorage.removeItem('__scoring_bluetooth_cache'); setSelected(null); }}>
              <Typography variant="caption">{t('scoring.bluetoothDisconnect')}</Typography>
            </Button>
          </Box>

          {events.length > 0 && (
            <Box>
              <Box sx={{ py: 1.5, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: "'Orbitron', sans-serif", fontSize: '2em', direction: 'ltr' }}>
                  {(events[eventsIndex || 0].time / 1000).toFixed(2)}
                </Typography>
                <Typography variant="caption" sx={{ direction: 'ltr' }}>
                  Shot: {(eventsIndex || 0) + 1}/{events.length}
                  &nbsp;&nbsp;Par: {!eventsIndex ? ' -' : ((events[eventsIndex].time - events[eventsIndex - 1].time) / 1000).toFixed(2)}
                </Typography>
              </Box>
              <Button
                fullWidth variant="contained" color="secondary" sx={{ mb: 1 }}
                onClick={() => onUpdateScore((events[eventsIndex || 0].time / 1000).toFixed(2))}
              >
                {t('scoring.bluetoothSetAsTime')}
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button variant="contained" color="primary" sx={{ flex: 1 }} disabled={events.length === 0}
              onClick={() => { setEventsIndex((eventsIndex + 1) % events.length); setCanStart(true); }}>
              {t('scoring.bluetoothReview')}
            </Button>
            <Button variant="contained" color="primary" sx={{ flex: 1 }} disabled={!canStart}
              onClick={onTimerStart}>
              {t('scoring.bluetoothStart')}
            </Button>
            <Button variant="contained" color="primary" sx={{ flex: 1 }} disabled={!canStart}
              onClick={onTimerReset}>
              {t('scoring.bluetoothReset')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
